import * as Sentry from '@sentry/react'
import { getDefaultFormState, type RJSFSchema } from '@rjsf/utils'
import { type AccountOpeningTypeSelector, type AccountType } from '../../../models/AccountOpening'
import { requiredRolesFor, roleSlotLabel } from './accountTypeCatalog'
import { partyKey } from './parties'
import { isRecord, titleCase, withoutBlanks, type SchemaNode } from './schemaValues'
import { WIZARD_DEFAULTS_BEHAVIOR, wizardValidator } from '../rjsf/wizardValidator'
import { type WizardAccount, type WizardFormContext } from './types'

export interface MissingItem {
  key: string
  label: string
  fieldId?: string
}

const childSchema = (node: SchemaNode | null, property: string): SchemaNode | null => {
  const properties: unknown = node?.properties
  if (!isRecord(properties)) {
    return null
  }
  const child: unknown = properties[property]
  return isRecord(child) ? child : null
}

const schemaFieldLabel = (schema: SchemaNode, propertyPath: string, missingProperty: string): string => {
  const segments = propertyPath.split('.').filter(Boolean)
  if (segments.length === 0) {
    return titleCase(missingProperty)
  }
  const node = segments.reduce<SchemaNode | null>((current, segment) => childSchema(current, segment), schema)
  const title: unknown = node?.title
  return typeof title === 'string' && title.length > 0 ? title : titleCase(segments[segments.length - 1])
}

const rjsfFieldId = (propertyPath: string): string => ['root', ...propertyPath.split('.').filter(Boolean)].join('_')

const asRendered = (schema: Record<string, unknown>, values: Record<string, unknown>): Record<string, unknown> =>
  (getDefaultFormState(wizardValidator, schema as RJSFSchema, values, schema as RJSFSchema, undefined, WIZARD_DEFAULTS_BEHAVIOR) as
    | Record<string, unknown>
    | undefined) ?? values

const schemaErrors = (
  schema: Record<string, unknown>,
  values: Record<string, unknown>,
  keep: (name: string | undefined) => boolean,
  describe: (label: string, message: string | undefined) => string,
  keyPrefix: string,
): MissingItem[] => {
  if (Object.keys(schema ?? {}).length === 0) {
    return []
  }
  try {
    return wizardValidator
      .validateFormData(withoutBlanks(asRendered(schema, values ?? {})), schema as RJSFSchema)
      .errors.filter((error) => keep(error.name))
      .map((error) => {
        const missingProperty = String((error.params as { missingProperty?: string } | undefined)?.missingProperty ?? '')
        const propertyPath = error.property ?? ''
        return {
          key: `${keyPrefix}:${error.name ?? 'invalid'}:${propertyPath || missingProperty}`,
          label: describe(schemaFieldLabel(schema, propertyPath, missingProperty), error.message),
          fieldId: rjsfFieldId(propertyPath || missingProperty),
        }
      })
  } catch (error) {
    Sentry.captureException(error)
    return []
  }
}

export const schemaMissingItems = (schema: Record<string, unknown>, values: Record<string, unknown>, keyPrefix: string): MissingItem[] =>
  schemaErrors(
    schema,
    values,
    (name) => name === 'required',
    (label) => label,
    keyPrefix,
  )

export const schemaInvalidItems = (schema: Record<string, unknown>, values: Record<string, unknown>, keyPrefix: string): MissingItem[] =>
  schemaErrors(
    schema,
    values,
    (name) => name !== 'required',
    (label, message) => (message ? `${label} ${message}` : label),
    keyPrefix,
  )

export const axisMissingItems = (selectors: AccountOpeningTypeSelector[], account: WizardAccount): MissingItem[] =>
  selectors.filter((selector) => !account.selection[selector.field]).map((selector) => ({ key: selector.field, label: selector.label }))

export const holderMissingItems = (accountTypes: AccountType[], account: WizardAccount): MissingItem[] =>
  requiredRolesFor(accountTypes, account.typeId).flatMap((required) => {
    const named = account.holders.filter((holder) => holder.roleCode === required.role && holder.name.trim().length > 0).length
    return Array.from({ length: Math.max(0, required.min - named) }, (_, offset) => ({
      key: `role:${required.role}:${named + offset}`,
      label: `${roleSlotLabel(required)} name`,
    }))
  })

export const holderConflictItems = (account: WizardAccount): MissingItem[] => {
  const items: MissingItem[] = []
  const firstAt = new Map<string, number>()
  account.holders.forEach((holder, index) => {
    const name = holder.name.trim()
    if (!name) {
      return
    }
    const key = `${partyKey(name)}|${holder.roleCode}`
    const earlier = firstAt.get(key)
    if (earlier === undefined) {
      firstAt.set(key, index)
      return
    }
    items.push({ key: `duplicate:${key}:${index}`, label: `${name} is named twice — holder ${index + 1} repeats holder ${earlier + 1}` })
  })
  return items
}

export const missingFieldContext = (items: MissingItem[]): WizardFormContext => ({
  missingFieldIds: new Set(items.map((item) => item.fieldId).filter((fieldId): fieldId is string => !!fieldId)),
})

export const isIntakeReady = (account: WizardAccount): boolean => !!account.typeId
